import { ESdChangeReason, IDisplayItem, IDocumentParameterDefinition } from "@kuika/kuika-cl-model";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import KuikaCollapsePanel from "../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import { countBoundDocumentParameters } from "../../document-parameters/document-parameter-binding";
import "./document-parameters-component-panel.scss";
import PropertyRowIcon from "./property-row-icon";

/** The component this panel belongs to, matched on the metadata name in `0182_Document.json`. */
const DOCUMENT_COMPONENT_NAME: string = "Document";

/** Collapse key; a stable literal because there is exactly one of these per selected component. */
const DOCUMENT_PARAMETERS_PANEL_KEY: string = "documentParametersPanel";

interface IDocumentParametersComponentPanelState {
  isExpanded: boolean;
}

/**
 * Document Parameters entry point on a Document component's properties (KOA-17254).
 *
 * <b>Why here as well as in the sources tree.</b> The tool is project-wide and is surveyed from the sources tree,
 * but parameters are almost always noticed from the other end: a document is placed on a screen, it renders with
 * `<SIRKET_ADI>` showing, and the question "where does that get filled" is asked about *this* component. Making the
 * answer reachable from the thing that raised it saves leaving the screen, and the popup returns to exactly where
 * it was opened from.
 *
 * <b>Why a panel rather than a bindable property.</b> Nothing here is a value the component takes — it opens an
 * editor for project-level configuration. Declaring it in the component metadata would put it in generated code and
 * in the property form's binding surface, where it would be meaningless. `parameterContext` is the component's real
 * parameter-related property, and it is already declared; this is a shortcut, not data.
 *
 * <b>Why it is built from the sibling panels' own markup.</b> Every other entry in this column — Mode, VersionId,
 * ParameterContext, Locale, OfficeEngine, Authorization, Visibility, Editability — is a box with its label on the
 * left and a chevron on the right. The kit's `KuikaCollapsePanel` on its own draws an unboxed header with a rule
 * beneath it, which is what this row once did and why it stood out. It now carries the same classes as its
 * siblings (`kuika-right-panel__collapse … --boxed`, right-hand chevron), so it matches them by construction.
 *
 * <b>Why the body looks like a property row.</b> Two earlier attempts invented their own furniture here — a paragraph and
 * a button, then a card with tag chips and a status line. Both were louder than everything around them, and the
 * chips in particular fought a panel this narrow: long tag names either truncated to uselessness or wrapped the
 * card open. This panel's own idiom is a label above a full-width 24px control, and that is what a single-value
 * summary opening an editor should be. The count line carries the whole state, and the one thing worth colouring —
 * unbound, which is why a document renders raw tags at a customer — is coloured inside it.
 */
class DocumentParametersComponentPanel extends PureSdComponent<IWithSDProps, IDocumentParametersComponentPanelState> {
  constructor(props: IWithSDProps) {
    super(props);
    this.state = { isExpanded: true };
  }

  private isDocumentSelected = (): boolean => {
    const screenDesign = this.props.sd.getScreenDesign();
    const selectedDi: IDisplayItem | undefined = this.props.sd.getSelectedDisplayItem(screenDesign);
    return selectedDi?.item?.name === DOCUMENT_COMPONENT_NAME;
  };

  private open = (): void => {
    this.props.sd.openDocumentParametersPopup();
  };

  /**
   * The control's text. One line covering every state, so the row never changes shape — a summary that grows a
   * second line when something is wrong makes the panel below it jump as parameters are bound.
   */
  private renderValue(definitions: IDocumentParameterDefinition[]) {
    if (definitions.length === 0) {
      return <span className="kuika-dp-row__value kuika-dp-row__value--empty">None defined</span>;
    }

    const unbound: number = definitions.length - countBoundDocumentParameters(definitions);

    return (
      <span className="kuika-dp-row__value">
        {`${definitions.length} defined`}
        {unbound > 0 && (
          <>
            <span className="kuika-dp-row__separator">·</span>
            <span className="kuika-dp-row__unbound" data-testid="documentParametersUnboundCount">
              {`${unbound} unbound`}
            </span>
          </>
        )}
      </span>
    );
  }

  private renderBody() {
    const definitions: IDocumentParameterDefinition[] = this.props.sd?.documentParameterDefinitions ?? [];

    return (
      <div className="kuika-dp-row">
        <span className="kuika-dp-row__label">Parameters</span>

        {/*
          A real button: this opens a dialog, and the panel is walked by keyboard as often as by mouse. Styled to
          the panel's control metrics rather than to a button, because in this column it reads as the property's
          value — a button chrome here would claim more importance than the row deserves.
        */}
        <button
          type="button"
          className="kuika-dp-row__control"
          data-testid="openDocumentParametersPopupButton"
          onClick={this.open}
        >
          {this.renderValue(definitions)}
          <KuikaSvg name="chevron-right" type="arrows" width={12} height={12} />
        </button>

        <KuikaSmallText className="kuika-dp-row__note">Shared by every document in this project.</KuikaSmallText>
      </div>
    );
  }

  render() {
    if (!this.isDocumentSelected()) return null;

    // The same collapse, classes and chevron side as Visibility / Editability, so the row matches them by
    // construction — see the class comment.
    return (
      <KuikaCollapsePanel
        activeKey={this.state.isExpanded ? DOCUMENT_PARAMETERS_PANEL_KEY : ""}
        onChange={() => this.setState({ isExpanded: !this.state.isExpanded })}
        iconPlacement="right"
        className="kuika-right-panel__collapse kuika-right-panel__collapse-lighter kuika-right-panel__collapse--boxed kuika-right-panel__collapse--document-parameters"
        items={[
          {
            key: DOCUMENT_PARAMETERS_PANEL_KEY,
            label: (
              <>
                <PropertyRowIcon kind="documentParameters" />
                <KuikaTextMedium>Document Parameters</KuikaTextMedium>
              </>
            ),
            children: <div className="kuika-right-panel__collapse-body">{this.renderBody()}</div>
          }
        ]}
      />
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return (
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.OtherSetStates
    );
  };
}

const documentParametersComponentPanel = withSD<{}>(DocumentParametersComponentPanel);
export { documentParametersComponentPanel as DocumentParametersComponentPanel };
